import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, LayoutGrid, Table as TableIcon, Crown } from 'lucide-react';
import { usePlans, SubscriptionPlan } from '@/hooks/usePlans';
import { SortablePlanCard } from '@/components/plans/SortablePlanCard';
import { PlanForm } from '@/components/plans/PlanForm';
import { PlanDeleteDialog } from '@/components/plans/PlanDeleteDialog';
import { PlansTable } from '@/components/plans/PlansTable';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

export default function Plans() {
  const { plans, isLoading, createPlan, updatePlan, deletePlan, togglePlanStatus, updatePlanOrder } = usePlans();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [planToDelete, setPlanToDelete] = useState<{ id: string; name: string } | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  useEffect(() => {
    document.title = 'Dashi | Planos';
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleCreate = () => {
    setSelectedPlan(null);
    setIsFormOpen(true);
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setIsFormOpen(true);
  };

  const handleSubmit = async (planData: Partial<SubscriptionPlan>) => {
    if (selectedPlan) {
      await updatePlan.mutateAsync({ id: selectedPlan.id, ...planData });
    } else {
      await createPlan.mutateAsync(planData as any);
    }
    setIsFormOpen(false);
    setSelectedPlan(null);
  };

  const handleDelete = (id: string, name: string) => {
    setPlanToDelete({ id, name });
  };

  const confirmDelete = async () => {
    if (planToDelete) {
      await deletePlan.mutateAsync(planToDelete.id);
      setPlanToDelete(null);
    }
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    await togglePlanStatus.mutateAsync({ id, is_active: isActive });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && plans) {
      const oldIndex = plans.findIndex((p) => p.id === active.id);
      const newIndex = plans.findIndex((p) => p.id === over.id);

      const newPlans = arrayMove(plans, oldIndex, newIndex);
      
      // Update display_order for all affected plans
      const updates = newPlans.map((plan, index) => ({
        id: plan.id,
        display_order: index + 1,
      }));

      updatePlanOrder.mutate(updates);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Crown className="h-8 w-8" />
            Gerenciamento de Planos
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure os planos de assinatura e seus limites
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && setViewMode(value as 'cards' | 'table')}>
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem 
                  value="cards" 
                  aria-label="Visualização em cards"
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  <LayoutGrid className="h-4 w-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>
                <p>Visualizar planos em formato de cards</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem 
                  value="table" 
                  aria-label="Visualização em tabela"
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  <TableIcon className="h-4 w-4" />
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>
                <p>Visualizar planos em formato de tabela</p>
              </TooltipContent>
            </Tooltip>
          </ToggleGroup>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Plano
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Criar um novo plano de assinatura</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {plans && plans.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground mb-4">Nenhum plano cadastrado ainda</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Plano
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Criar seu primeiro plano de assinatura</p>
            </TooltipContent>
          </Tooltip>
        </div>
      ) : viewMode === 'table' ? (
        <PlansTable
          plans={plans || []}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleStatus={handleToggleStatus}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={plans?.map((p) => p.id) || []}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans?.map((plan) => (
                <SortablePlanCard
                  key={plan.id}
                  plan={plan}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete(id, plan.name)}
                  onToggleStatus={handleToggleStatus}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <PlanForm
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedPlan(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedPlan}
        isLoading={createPlan.isPending || updatePlan.isPending}
      />

      <PlanDeleteDialog
        open={!!planToDelete}
        onClose={() => setPlanToDelete(null)}
        onConfirm={confirmDelete}
        planName={planToDelete?.name || ''}
      />
    </div>
    </TooltipProvider>
  );
}
